import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import InstrumentStock from './InstrumentStock';
import InstrumentShowcase from './InstrumentShowcase';
import { getInstruments, getInstrumentsPaginated, Instrument } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCanAccessInstrumentStock } from '../hooks/useCanAccessInstrumentStock';
import { MOCK_INSTRUMENTS } from '../data/instrumentMock';
import './MemberManagement.css';
import './InstrumentShowcase.css';

const USE_INSTRUMENT_PREVIEW_MOCK =
  process.env.NODE_ENV === 'development' && process.env.REACT_APP_INSTRUMENT_MOCK !== '0';

const InstrumentManagement: React.FC = () => {
  const { isDemoBrowseOnly } = useAuth();
  const { canAccessStock, loading: stockAccessLoading } = useCanAccessInstrumentStock();
  const location = useLocation();
  const navigate = useNavigate();
  const isStockPath = location.pathname.endsWith('/stock');
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  /** 下一页页码（首屏为 1，加载完成后指向待请求的页） */
  const [nextPage, setNextPage] = useState(2);
  const loadMoreInFlight = useRef(false);
  /** 全量列表仅用于展示页统计卡（与分页数据独立） */
  const [statInstruments, setStatInstruments] = useState<Instrument[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isDemoBrowseOnly) {
        if (!cancelled) setStatInstruments(MOCK_INSTRUMENTS);
        return;
      }
      try {
        const r = await getInstruments();
        if (!cancelled && r.success && r.data && r.data.length > 0) {
          setStatInstruments(r.data);
        } else if (!cancelled && USE_INSTRUMENT_PREVIEW_MOCK) {
          setStatInstruments(MOCK_INSTRUMENTS);
        } else if (!cancelled) {
          setStatInstruments([]);
        }
      } catch {
        if (!cancelled) {
          setStatInstruments(USE_INSTRUMENT_PREVIEW_MOCK ? MOCK_INSTRUMENTS : []);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isDemoBrowseOnly]);

  const fetchFirstPage = useCallback(async () => {
    setLoading(true);
    setNextPage(2);
    if (isDemoBrowseOnly) {
      const tot = MOCK_INSTRUMENTS.length;
      setInstruments(MOCK_INSTRUMENTS.slice(0, pageSize));
      setTotal(tot);
      setLoading(false);
      return;
    }
    try {
      const response = await getInstrumentsPaginated({ page: 1, pageSize });
      if (response.success && response.data) {
        const { items, total: apiTotal } = response.data;
        const useMock =
          USE_INSTRUMENT_PREVIEW_MOCK &&
          apiTotal === 0 &&
          (!items || items.length === 0);
        if (useMock) {
          const tot = MOCK_INSTRUMENTS.length;
          const slice = MOCK_INSTRUMENTS.slice(0, pageSize);
          setInstruments(slice);
          setTotal(tot);
        } else {
          setInstruments(items || []);
          setTotal(apiTotal);
        }
      } else if (USE_INSTRUMENT_PREVIEW_MOCK) {
        const tot = MOCK_INSTRUMENTS.length;
        setInstruments(MOCK_INSTRUMENTS.slice(0, pageSize));
        setTotal(tot);
      } else {
        setInstruments([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('获取仪器列表失败:', error);
      if (USE_INSTRUMENT_PREVIEW_MOCK) {
        const tot = MOCK_INSTRUMENTS.length;
        setInstruments(MOCK_INSTRUMENTS.slice(0, pageSize));
        setTotal(tot);
      } else {
        setInstruments([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
    }
  }, [pageSize, isDemoBrowseOnly]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || loadMoreInFlight.current) return;
    if (instruments.length >= total || total === 0) return;
    loadMoreInFlight.current = true;
    setLoadingMore(true);
    if (isDemoBrowseOnly) {
      try {
        const start = (nextPage - 1) * pageSize;
        const slice = MOCK_INSTRUMENTS.slice(start, start + pageSize);
        setInstruments((prev) => {
          const ids = new Set(prev.map((i) => i._id).filter(Boolean));
          const extra = slice.filter((i) => !i._id || !ids.has(i._id));
          return [...prev, ...extra];
        });
        setTotal(MOCK_INSTRUMENTS.length);
        setNextPage((p) => p + 1);
      } finally {
        loadMoreInFlight.current = false;
        setLoadingMore(false);
      }
      return;
    }
    try {
      const response = await getInstrumentsPaginated({ page: nextPage, pageSize });
      if (response.success && response.data) {
        const { items, total: apiTotal } = response.data;
        const useMock =
          USE_INSTRUMENT_PREVIEW_MOCK &&
          apiTotal === 0 &&
          (!items || items.length === 0);
        if (useMock) {
          const start = (nextPage - 1) * pageSize;
          const slice = MOCK_INSTRUMENTS.slice(start, start + pageSize);
          setInstruments((prev) => {
            const ids = new Set(prev.map((i) => i._id).filter(Boolean));
            const extra = slice.filter((i) => !i._id || !ids.has(i._id));
            return [...prev, ...extra];
          });
          setTotal(MOCK_INSTRUMENTS.length);
        } else {
          setInstruments((prev) => {
            const ids = new Set(prev.map((i) => i._id).filter(Boolean));
            const extra = (items || []).filter((i) => i._id && !ids.has(i._id));
            return [...prev, ...extra];
          });
          setTotal(apiTotal);
        }
        setNextPage((p) => p + 1);
      }
    } catch (error) {
      console.error('加载更多仪器失败:', error);
    } finally {
      loadMoreInFlight.current = false;
      setLoadingMore(false);
    }
  }, [loading, loadingMore, instruments.length, total, nextPage, pageSize, isDemoBrowseOnly]);

  useEffect(() => {
    fetchFirstPage();
  }, [fetchFirstPage]);

  useEffect(() => {
    if (isStockPath && !stockAccessLoading && !canAccessStock) {
      navigate('/instruments', { replace: true });
    }
  }, [isStockPath, canAccessStock, stockAccessLoading, navigate]);

  const hasMore = instruments.length < total;
  const loadedMeta = `${instruments.length} / ${total} 台`;

  return (
    <div className="page-container full-height members-lims">
      {isStockPath && canAccessStock ? (
        <InstrumentStock />
      ) : loading && instruments.length === 0 ? (
        <div className="d-flex justify-content-center my-5 py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <InstrumentShowcase
            data={instruments}
            loading={loading}
            listInitialEmpty={instruments.length === 0}
            loadingMore={loadingMore}
            hasMore={hasMore}
            onLoadMore={loadMore}
            onRefresh={fetchFirstPage}
            statsInstruments={statInstruments.length ? statInstruments : instruments}
            totalCount={total}
          />
          <div className="is-pagination-bar">
            <span className="is-page-meta">
              已加载 {loadedMeta}
              {hasMore ? ' · 下滑自动加载更多' : total > 0 ? ' · 已全部加载' : ''}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default InstrumentManagement;
